import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { materialService } from '../../services/materialService';
import { useReferenceCurrencies, useReferenceUnits } from '../../hooks';
import { formatUnit } from '../../utils/formatUnit';
import {
  buildCurrencyOptions,
  buildUnitOptions,
  conversionParams,
  conversionTarget,
  resolveSeriesDisplay,
} from '../../utils/chartConversion';

const timeFrames = [
  { id: '1h', label: '1h' },
  { id: '3h', label: '3h' },
  { id: '1d', label: '1d' },
  { id: '1w', label: '1w' },
  { id: '1m', label: '1m' },
  { id: '3m', label: '3m' },
  { id: '1y', label: '1y' },
  { id: '5y', label: '5y' },
  { id: 'max', label: 'max' },
  { id: 'custom', label: 'Custom' }
];
const hiddenTimeFrames = new Set(['1h', '3h', '1d']);
const visibleTimeFrames = timeFrames.filter((tf) => !hiddenTimeFrames.has(tf.id));

const ExportPopup = ({
  isOpen,
  onClose,
  materialName,
  materialDate,
  materialPrice,
  materialCode,
  startDate,
  endDate,
  timeframe,
  defaultFrequency,
  sourceCurrency,
  sourceUnit,
  initialTarget,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage || i18n?.language || 'en';
  const { data: referenceCurrencies } = useReferenceCurrencies({ enabled: !!isOpen });
  const { data: referenceUnits } = useReferenceUnits({ enabled: !!isOpen });
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(timeframe || '1y');
  const [sortOrder, setSortOrder] = useState('ascending');
  const [frequency, setFrequency] = useState(defaultFrequency ? defaultFrequency.charAt(0).toUpperCase() + defaultFrequency.slice(1) : 'Frequency');
  // Ziel wie im Chart (null = Original); umgerechnet wird im Backend
  const [currency, setCurrency] = useState(initialTarget?.currency || null);
  const [unit, setUnit] = useState(initialTarget?.unit || null);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [additionalInclusions, setAdditionalInclusions] = useState({
    forecast: true,
    news: false,
    xxx: false
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (timeframe) {
      setSelectedTimeFrame(hiddenTimeFrames.has(timeframe) ? '1w' : timeframe);
    }
  }, [timeframe]);

  useEffect(() => {
    if (defaultFrequency) {
      const label = defaultFrequency.charAt(0).toUpperCase() + defaultFrequency.slice(1);
      setFrequency(label);
    }
  }, [defaultFrequency]);

  useEffect(() => {
    setCurrency(initialTarget?.currency || null);
    setUnit(initialTarget?.unit || null);
  }, [initialTarget?.currency, initialTarget?.unit, isOpen]);

  if (!isOpen) return null;

  const currencyChoice = buildCurrencyOptions({ currencies: referenceCurrencies, sourceCurrency, language });
  const unitChoice = buildUnitOptions({ units: referenceUnits, sourceUnit, language });
  const hintText = (choice) => (choice.hint
    ? t(`charts.conversion.hint.${choice.hint}`, { code: sourceCurrency || '', unit: formatUnit(sourceUnit) || sourceUnit || '' })
    : null);
  const currencyHint = hintText(currencyChoice);
  const unitHint = hintText(unitChoice);
  const sourceCurrencyLabel = sourceCurrency || '';
  const selectedUnitOption = unit ? unitChoice.options.find((o) => o.value === unit) : null;
  const selectedUnitLabel = selectedUnitOption?.short || unit || formatUnit(sourceUnit);

  const handleInclusionToggle = (key) => {
    setAdditionalInclusions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toCsv = (rows) =>
    rows
      .map((row) =>
        row
          .map((value) => {
            const v = value === null || value === undefined ? '' : String(value);
            return `"${v.replace(/"/g, '""')}"`;
          })
          .join(',')
      )
      .join('\n');

  const handleExport = async () => {
    if (!materialCode) {
      setExportError('No material selected for export.');
      return;
    }
    setIsExporting(true);
    setExportError(null);

    const startIso = (startDate || new Date()).toISOString();
    const endIso = (endDate || new Date()).toISOString();
    const freqParam = frequency && frequency !== 'Frequency' ? frequency.toLowerCase() : undefined;
    const tfParam =
      selectedTimeFrame === 'max'
        ? '1m'
        : selectedTimeFrame === 'custom'
          ? '1d'
          : selectedTimeFrame;

    const exportTarget = conversionTarget({ targetCurrency: currency, targetUnit: unit, baseCurrency: sourceCurrency, baseUnit: sourceUnit });
    const exportParams = conversionParams(exportTarget);

    try {
      const payload = await materialService.exportChart(materialCode, {
        start: startIso,
        end: endIso,
        tf: tfParam,
        frequency: freqParam,
        sort: sortOrder === 'descending' ? 'desc' : 'asc',
        ...exportParams,
        include_forecast: additionalInclusions.forecast,
        include_news: additionalInclusions.news,
      });

      // Waehrung/Einheit laut Backend; ohne Umrechnung bleibt es beim Original
      const display = resolveSeriesDisplay({
        sourceCurrency: payload?.meta?.source_currency || payload?.currency || sourceCurrency,
        sourceUnit: payload?.meta?.source_unit || payload?.unit || sourceUnit,
        target: exportTarget,
        meta: payload?.meta,
      });
      const metaCurrency = display.currency;
      const metaUnit = display.unit;
      const rows = [
        ['Date', 'Open', 'High', 'Low', 'Close', 'Volume', 'Currency', 'Unit'],
        ...(payload?.data || []).map((item) => [
          item.date,
          item.open,
          item.high,
          item.low,
          item.close ?? item.value,
          item.volume,
          metaCurrency,
          metaUnit,
        ]),
      ];

      if (additionalInclusions.forecast && payload?.forecast?.data?.length) {
        rows.push([]);
        rows.push(['Forecast']);
        rows.push(['Year', 'Low Estimate', 'Average Estimate', 'High Estimate']);
        payload.forecast.data.forEach((f) => {
          rows.push([f.year, f.lowEstimate, f.averageEst, f.highEstimate]);
        });
      }

      const csvContent = toCsv(rows);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${materialCode || 'material'}-export.csv`;
      link.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch (error) {
      console.error('[ExportPopup] Failed to export data', error);
      setExportError(error?.message || 'Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="export-dropdown-popup" ref={popupRef}>
        <h2 className="export-popup-title">Export Settings</h2>

        {/* Product Info */}
        <div className="export-product-info">
          <div className="export-product-indicator"></div>
          <div className="export-product-details">
            <div className="export-product-name">{materialName || 'Material'}</div>
            <div className="export-product-date">{materialDate || 'Date not available'}</div>
          </div>
          <div className="export-product-price">
            {materialPrice || '—'}
          </div>
        </div>

        {/* Time Frame Pills and Controls Row */}
        <div className="export-timeframes-and-controls">
          <div className="export-timeframes">
            {visibleTimeFrames.map((tf) => (
              <button
                key={tf.id}
                onClick={() => setSelectedTimeFrame(tf.id)}
                className={`export-timeframe-pill ${selectedTimeFrame === tf.id ? 'active' : ''}`}
              >
                {tf.label}
              </button>
            ))}
          </div>

          <div className="export-controls-row">
          <div className="export-dropdown-container">
            <button
              className="export-dropdown-btn"
              onClick={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
            >
              {frequency}
              <ChevronDown size={16} />
            </button>
            {showFrequencyDropdown && (
              <div className="export-dropdown-menu">
                <div className="export-dropdown-item" onClick={() => { setFrequency('Daily'); setShowFrequencyDropdown(false); }}>Daily</div>
                <div className="export-dropdown-item" onClick={() => { setFrequency('Weekly'); setShowFrequencyDropdown(false); }}>Weekly</div>
                <div className="export-dropdown-item" onClick={() => { setFrequency('Monthly'); setShowFrequencyDropdown(false); }}>Monthly</div>
              </div>
            )}
          </div>

          <div className="export-dropdown-container">
            <button
              className="export-dropdown-btn currency-btn"
              disabled={currencyChoice.disabled}
              title={currencyHint || undefined}
              onClick={() => !currencyChoice.disabled && setShowCurrencyDropdown(!showCurrencyDropdown)}
            >
              <span className="currency-symbol">$</span>
              {currency || sourceCurrencyLabel || 'Currency'}
              <ChevronDown size={16} />
            </button>
            {showCurrencyDropdown && !currencyChoice.disabled && (
              <div className="export-dropdown-menu">
                {currencyChoice.options.map((option) => (
                  <div
                    key={option.value || '__original'}
                    className="export-dropdown-item"
                    onClick={() => { setCurrency(option.value); setShowCurrencyDropdown(false); }}
                  >
                    {option.isOriginal ? t('charts.conversion.original', { code: option.code }) : option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="export-dropdown-container">
            <button
              className="export-dropdown-btn"
              disabled={unitChoice.disabled}
              title={unitHint || undefined}
              onClick={() => !unitChoice.disabled && setShowUnitDropdown(!showUnitDropdown)}
            >
              {selectedUnitLabel || 'Unit'}
              <ChevronDown size={16} />
            </button>
            {showUnitDropdown && !unitChoice.disabled && (
              <div className="export-dropdown-menu">
                {unitChoice.options.map((option) => (
                  <div
                    key={option.value || '__original'}
                    className="export-dropdown-item"
                    onClick={() => { setUnit(option.value); setShowUnitDropdown(false); }}
                  >
                    {option.isOriginal ? t('charts.conversion.original', { code: formatUnit(option.code) }) : option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Sort Order */}
        <div className="export-sort-section">
          <div className="export-sort-container">
            <button
              className={`export-sort-btn ${sortOrder === 'ascending' ? 'active' : ''}`}
              onClick={() => setSortOrder('ascending')}
            >
              Ascending
            </button>
            <button
              className={`export-sort-btn ${sortOrder === 'descending' ? 'active' : ''}`}
              onClick={() => setSortOrder('descending')}
            >
              Descending
            </button>
          </div>
        </div>

        {/* Additional Inclusion */}
        <div className="export-inclusion-section">
          <h3 className="export-inclusion-title">Additional Inclusion</h3>
          <div className="export-inclusion-pills">
            <button
              className={`export-inclusion-pill ${additionalInclusions.forecast ? 'active' : ''}`}
              onClick={() => handleInclusionToggle('forecast')}
            >
              Forecast
            </button>
            <button
              className={`export-inclusion-pill ${additionalInclusions.news ? 'active' : ''}`}
              onClick={() => handleInclusionToggle('news')}
            >
              News
            </button>
            <button
              className={`export-inclusion-pill ${additionalInclusions.xxx ? 'active' : ''}`}
              onClick={() => handleInclusionToggle('xxx')}
            >
              xxx
            </button>
          </div>
        </div>

        {exportError && <div className="export-error">{exportError}</div>}

        <div
          className="export-actions"
          style={{ display: 'flex', gap: '10px', marginTop: '12px' }}
        >
          <button
            className="export-action-btn export-inclusion-pill"
            onClick={handleExport}
            disabled={isExporting}
            type="button"
            style={{ padding: '12px 18px', minWidth: '140px', fontWeight: 600 }}
          >
            {isExporting ? 'Preparing…' : 'Export CSV'}
          </button>
          <button
            className="export-action-cancel export-inclusion-pill"
            onClick={onClose}
            type="button"
            style={{ padding: '12px 18px', minWidth: '120px', fontWeight: 600 }}
          >
            Cancel
          </button>
        </div>
    </div>
  );
};

export default ExportPopup;
